import React, { useState, useEffect, useCallback, useRef } from "react";
import PageHeader from "./PageHeader";
import {
  Plus,
  Send,
  Loader,
  Sparkles,
  Trash2,
  FileText,
  Mail,
  MessageSquare,
  Check,
  ExternalLink,
  Building2,
  User,
} from "lucide-react";

const TEXT_TYPES = [
  { id: "stellenanzeige", label: "Stellenanzeige", icon: FileText, desc: "Job-Anzeige nach UNEQ-Linie" },
  { id: "email", label: "E-Mail", icon: Mail, desc: "Marketing- oder Bewerber-Mail" },
  { id: "marketing", label: "Marketing-Text", icon: MessageSquare, desc: "Salespage, Anzeige, Post – gegen die UNEQ-Markensprache prüfen" },
];

const FARBTYPEN = [
  "rot-gelb", "gelb-rot", "blau-gruen", "gruen-blau",
  "blau-rot", "rot-blau", "gelb-gruen", "gruen-gelb",
];
const farbtypLabel = (k) =>
  k.split("-").map((p) => p.replace("gruen", "Grün").replace(/^./, (c) => c.toUpperCase())).join("-");

// Liefert den Anzeige-Text einer Fund-Stelle (robust gegen die verschiedenen Issue-Formen).
function issueSubject(i) {
  return i.original || i.passage || i.sentence || i.word || i.generated || i.missing || i.violation || "";
}

function ReviewResult({ payload }) {
  const issues = payload.issues || [];
  const counts = payload.counts || { total: issues.length, critical: 0, soft: issues.length };
  if (issues.length === 0) {
    return (
      <div className="chat-review-clean">
        <Check size={16} /> Keine Probleme gefunden — der Text erfüllt die Kriterien.
      </div>
    );
  }
  // Nach Agent gruppieren
  const byAgent = {};
  for (const i of issues) {
    const a = i.agent || "Sonstiges";
    (byAgent[a] = byAgent[a] || []).push(i);
  }
  return (
    <div className="chat-review">
      <div className="chat-review-summary">
        <span className="chat-badge chat-badge-total">{counts.total} Funde</span>
        {counts.critical > 0 && <span className="chat-badge chat-badge-crit">{counts.critical} kritisch</span>}
        {counts.soft > 0 && <span className="chat-badge chat-badge-soft">{counts.soft} Stil</span>}
        {payload.hasSource === false && payload.textType === "stellenanzeige" && (
          <span className="chat-review-note">Ohne Quelle geprüft — kein Faktencheck.</span>
        )}
      </div>
      {Object.entries(byAgent).map(([agent, list]) => (
        <div key={agent} className="chat-review-group">
          <div className="chat-review-group-head">{agent} <span>({list.length})</span></div>
          {list.map((i, idx) => (
            <div key={idx} className="chat-review-issue">
              {i.severity === "critical" && <span className="chat-issue-dot crit" title="kritisch" />}
              <div className="chat-issue-body">
                {issueSubject(i) && <div className="chat-issue-orig">„{issueSubject(i)}"</div>}
                {i.problem && <div className="chat-issue-problem">{i.problem}</div>}
                {i.suggestion && <div className="chat-issue-suggestion">→ {i.suggestion}</div>}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function GenerationResult({ payload, onNavigate }) {
  const sections = payload.sections || [];
  const isJob = payload.textType === "stellenanzeige";

  const openInEditor = () => {
    if (payload.stellenanzeige_id) {
      window.history.pushState(null, "", `/jobs`);
      onNavigate && onNavigate("jobs");
    }
  };

  return (
    <div className="chat-genresult">
      <div className="chat-genresult-head">
        <Sparkles size={15} /> Generierter Text
        {isJob && payload.cost_eur != null && (
          <span className="chat-genresult-meta">{Number(payload.cost_eur).toFixed(2)} €</span>
        )}
      </div>
      <div className="chat-genresult-sections">
        {sections.map((s, idx) => (
          <div key={idx} className="chat-gen-section">
            <div className="chat-gen-section-name">{s.name}</div>
            <div className="chat-gen-section-text">
              {isJob ? (
                <>
                  {s.headline && <p><strong>{s.headline}</strong></p>}
                  {s.einleitung && <p>{s.einleitung}</p>}
                  {s.body && <p>{s.body}</p>}
                  {Array.isArray(s.items) && s.items.map((it, k) => (
                    <p key={k}>• {typeof it === "string" ? it : (it.text || it.content || "")}</p>
                  ))}
                </>
              ) : (
                <p>{s.content}</p>
              )}
            </div>
          </div>
        ))}
      </div>
      {isJob && payload.stellenanzeige_id && (
        <button className="btn-secondary-sm" onClick={openInEditor}>
          <ExternalLink size={14} /> Im Editor öffnen
        </button>
      )}
    </div>
  );
}

function OptionCard({ icon: Icon, title, desc, active, onClick }) {
  return (
    <button type="button" className={"chat-opt" + (active ? " is-active" : "")} onClick={onClick}>
      {Icon && <div className="chat-opt-icon"><Icon size={18} /></div>}
      <div className="chat-opt-body">
        <span className="chat-opt-title">{title}</span>
        {desc && <span className="chat-opt-desc">{desc}</span>}
      </div>
      <span className="chat-opt-check"><Check size={13} strokeWidth={3} /></span>
    </button>
  );
}

function Message({ m, onNavigate }) {
  if (m.role === "user") {
    return (
      <div className="chat-msg chat-msg-user">
        <div className="chat-avatar chat-avatar-user"><User size={15} /></div>
        <div className="chat-bubble chat-bubble-user">
          {m.kind === "review_input" ? (
            <div className="chat-review-input">
              <span className="chat-review-input-label">Zu prüfender Text:</span>
              <div className="chat-review-input-text">{m.content}</div>
            </div>
          ) : (
            <div className="chat-msg-text">{m.content}</div>
          )}
        </div>
      </div>
    );
  }
  const payload = m.payload || {};
  return (
    <div className="chat-msg chat-msg-assistant">
      <div className="chat-avatar chat-avatar-assistant"><Sparkles size={15} /></div>
      <div className="chat-bubble chat-bubble-assistant">
        {m.content && <div className="chat-msg-text">{m.content}</div>}
        {m.kind === "questions" && Array.isArray(payload.questions) && (
          <ul className="chat-questions">
            {payload.questions.map((q, i) => <li key={i}>{q}</li>)}
          </ul>
        )}
        {m.kind === "review_result" && <ReviewResult payload={payload} />}
        {m.kind === "generation_result" && <GenerationResult payload={payload} onNavigate={onNavigate} />}
      </div>
    </div>
  );
}

export default function ChatPage({ onNavigate }) {
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null); // number | "new" | null
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState(null);
  const threadRef = useRef(null);

  // Wizard-State (neuer Chat)
  const [wTextType, setWTextType] = useState("stellenanzeige");
  const [wTarget, setWTarget] = useState("partner");
  const [wPartnerId, setWPartnerId] = useState("");
  const [wPartnerName, setWPartnerName] = useState("");
  const [wFarbtyp, setWFarbtyp] = useState("rot-gelb");
  const [wMode, setWMode] = useState("review");
  const [partners, setPartners] = useState([]);

  // Eingabe-State (aktiver Chat)
  const [input, setInput] = useState("");
  const [pasteText, setPasteText] = useState("");
  const [pasteContext, setPasteContext] = useState("");

  const loadSessions = useCallback(() => {
    fetch("/api/chat/sessions", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setSessions(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  // Partner laden, sobald der Assistent geöffnet ist
  useEffect(() => {
    if (activeId === "new" && wTarget === "partner" && partners.length === 0) {
      fetch("/api/partners", { credentials: "include" })
        .then((r) => (r.ok ? r.json() : []))
        .then((d) => setPartners(Array.isArray(d) ? d : []))
        .catch(() => {});
    }
  }, [activeId, wTarget, partners.length]);

  const loadSession = useCallback((id) => {
    fetch(`/api/chat/sessions/${id}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d) { setSession(d.session); setMessages(d.messages || []); }
      })
      .catch(() => {});
  }, []);

  const openSession = (id) => {
    setActiveId(id);
    setInput(""); setPasteText(""); setPasteContext(""); setStage(null);
    loadSession(id);
  };

  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [messages, stage]);

  const startNewChat = () => {
    setActiveId("new");
    setSession(null);
    setMessages([]);
    setStage(null);
  };

  const createSession = async () => {
    if (wTarget === "partner" && !wPartnerName.trim() && !wPartnerId) {
      alert("Bitte einen Partner wählen oder einen Firmennamen eingeben.");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch("/api/chat/sessions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          textType: wTextType,
          mode: wMode,
          target: wTarget,
          partnerId: wTarget === "partner" ? (wPartnerId || null) : null,
          partnerName: wTarget === "partner" ? (wPartnerName || null) : null,
          farbtyp: wTextType === "stellenanzeige" ? wFarbtyp : null,
        }),
      });
      const s = await res.json();
      if (!res.ok) throw new Error(s.error || "Fehler");
      loadSessions();
      openSession(s.id);
    } catch (err) {
      alert("Fehler: " + err.message);
    } finally {
      setBusy(false);
    }
  };

  const deleteSession = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Diesen Chat löschen?")) return;
    await fetch(`/api/chat/sessions/${id}`, { method: "DELETE", credentials: "include" });
    if (activeId === id) { setActiveId(null); setSession(null); setMessages([]); }
    loadSessions();
  };

  // Generieren-Modus: Nachricht/Antwort senden → Vollständigkeits-Check
  const sendMessage = async () => {
    if (!input.trim() || busy) return;
    setBusy(true);
    const text = input;
    setInput("");
    try {
      const res = await fetch(`/api/chat/sessions/${session.id}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ text }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Fehler");
      loadSession(session.id);
      loadSessions();
    } catch (err) {
      alert("Fehler: " + err.message);
    } finally {
      setBusy(false);
    }
  };

  // SSE-Lauf (Generierung oder Prüfung)
  const runPipeline = async (body) => {
    setBusy(true);
    setStage("start");
    try {
      const res = await fetch(`/api/chat/sessions/${session.id}/run`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body || {}),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.stage === "error") throw new Error(data.error || "Fehler");
            if (data.stage === "done") { setStage(null); }
            else setStage(data.stage);
          } catch (e) {
            if (e.message && e.message !== "Unexpected end of JSON input") { /* ignore parse noise */ }
          }
        }
      }
    } catch (err) {
      alert("Fehler: " + err.message);
    } finally {
      setStage(null);
      setBusy(false);
      loadSession(session.id);
      loadSessions();
    }
  };

  const runReview = () => {
    if (!pasteText.trim()) return;
    runPipeline({ text: pasteText, context: pasteContext });
    setPasteText(""); setPasteContext("");
  };

  const hasResult = messages.some((m) => m.kind === "review_result" || m.kind === "generation_result");
  const isReady = session && session.state === "ready";

  // ---------- Render ----------
  return (
    <>
      <PageHeader
        eyebrow="Texte"
        title="Text-Chat"
        subtitle="Marketing-Texte prüfen oder generieren lassen — nach den UNEQ-Kriterien je Textart. Deine Chats sind privat."
      />

      <div className="chat-layout">
        {/* Sidebar: Chat-Liste */}
        <div className="chat-sidebar card">
          <button className="btn-primary chat-new-btn" onClick={startNewChat}>
            <Plus size={16} /> Neuer Chat
          </button>
          <div className="chat-session-list">
            {sessions.length === 0 && <div className="chat-session-empty">Noch keine Chats.</div>}
            {sessions.map((s) => {
              const TIcon = s.text_type === "marketing" ? MessageSquare : s.text_type === "email" ? Mail : FileText;
              return (
                <div
                  key={s.id}
                  className={"chat-session-row" + (activeId === s.id ? " is-active" : "")}
                  onClick={() => openSession(s.id)}
                >
                  <TIcon size={14} />
                  <div className="chat-session-info">
                    <span className="chat-session-title">{s.title || "Neuer Chat"}</span>
                    <span className="chat-session-sub">
                      {s.mode === "generate" ? "Generieren" : "Prüfen"} · {s.target === "uneq" ? "UNEQ" : (s.partner_name || "Partner")}
                    </span>
                  </div>
                  <button className="chat-session-del" onClick={(e) => deleteSession(s.id, e)} title="Löschen">
                    <Trash2 size={13} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Hauptbereich */}
        <div className="chat-main card">
          {activeId === null && (
            <div className="chat-placeholder">
              <div className="chat-placeholder-icon"><MessageSquare size={32} strokeWidth={1.75} /></div>
              <p>Wähle links einen Chat oder starte einen neuen.</p>
            </div>
          )}

          {activeId === "new" && (
            <div className="chat-wizard">
              <div className="chat-wizard-header">
                <div className="chat-wizard-badge"><Sparkles size={20} /></div>
                <h3 className="chat-wizard-title">Neuer Chat</h3>
                <p className="chat-wizard-lead">Sag mir, worum es geht — dann prüfe oder schreibe ich deinen Text nach den UNEQ-Kriterien.</p>
              </div>

              <div className="chat-wizard-field">
                <label>Textart</label>
                <div className="chat-opt-grid">
                  {TEXT_TYPES.map((t) => (
                    <OptionCard
                      key={t.id}
                      icon={t.icon}
                      title={t.label}
                      desc={t.desc}
                      active={wTextType === t.id}
                      onClick={() => { setWTextType(t.id); if (t.id === "marketing") setWMode("review"); }}
                    />
                  ))}
                </div>
              </div>

              <div className="chat-wizard-field">
                <label>Für wen?</label>
                <div className="chat-opt-grid">
                  <OptionCard icon={Building2} title="UNEQ" desc="Für die eigene Marke" active={wTarget === "uneq"} onClick={() => setWTarget("uneq")} />
                  <OptionCard icon={User} title="Partner" desc="Für ein Partner-Unternehmen" active={wTarget === "partner"} onClick={() => setWTarget("partner")} />
                </div>
              </div>

              {wTarget === "partner" && (
                <div className="chat-wizard-field">
                  <label>Partner</label>
                  <div className="chat-select-wrap">
                    <select
                      className="chat-select"
                      value={wPartnerId}
                      onChange={(e) => {
                        setWPartnerId(e.target.value);
                        const p = partners.find((x) => x.partner_id === e.target.value);
                        setWPartnerName(p ? p.unternehmen : "");
                      }}
                    >
                      <option value="">— Partner wählen —</option>
                      {partners.map((p) => (
                        <option key={p.partner_id} value={p.partner_id}>{p.unternehmen}</option>
                      ))}
                    </select>
                  </div>
                  <input
                    className="chat-input-line"
                    type="text"
                    placeholder="… oder neuen Firmennamen eingeben"
                    value={wPartnerId ? "" : wPartnerName}
                    disabled={!!wPartnerId}
                    onChange={(e) => setWPartnerName(e.target.value)}
                  />
                </div>
              )}

              {wTextType === "stellenanzeige" && (
                <div className="chat-wizard-field">
                  <label>Persönlichkeitstyp (Farbtyp)</label>
                  <div className="chat-select-wrap">
                    <select className="chat-select" value={wFarbtyp} onChange={(e) => setWFarbtyp(e.target.value)}>
                      {FARBTYPEN.map((f) => <option key={f} value={f}>{farbtypLabel(f)}</option>)}
                    </select>
                  </div>
                </div>
              )}

              <div className="chat-wizard-field">
                <label>Was möchtest du tun?</label>
                <div className="chat-opt-grid">
                  <OptionCard icon={Check} title="Text prüfen" desc="Fertigen Text einfügen, ich checke ihn" active={wMode === "review"} onClick={() => setWMode("review")} />
                  {wTextType !== "marketing" && (
                    <OptionCard icon={Sparkles} title="Text generieren" desc="Du beschreibst, die KI schreibt" active={wMode === "generate"} onClick={() => setWMode("generate")} />
                  )}
                </div>
                {wTextType === "marketing" && (
                  <p className="chat-field-hint">Marketing-Texte werden aktuell geprüft (Feedback + Verbesserungsvorschläge), nicht generiert.</p>
                )}
              </div>

              <button className="btn-primary chat-wizard-cta" onClick={createSession} disabled={busy}>
                {busy ? <><Loader size={16} className="spin" /> Starte …</> : <><Sparkles size={16} /> Chat starten</>}
              </button>
            </div>
          )}

          {session && activeId !== "new" && (
            <>
              <div className="chat-thread-head">
                <span className="chat-thread-title">
                  {session.text_type === "email" ? "E-Mail" : session.text_type === "marketing" ? "Marketing-Text" : "Stellenanzeige"} · {session.mode === "generate" ? "Generieren" : "Prüfen"} · {session.target === "uneq" ? "UNEQ" : (session.partner_name || "Partner")}
                  {session.text_type === "stellenanzeige" && session.farbtyp ? ` · ${farbtypLabel(session.farbtyp)}` : ""}
                </span>
              </div>

              <div className="chat-thread" ref={threadRef}>
                {messages.length === 0 && session.mode === "generate" && (
                  <div className="chat-msg chat-msg-assistant">
                    <div className="chat-avatar chat-avatar-assistant"><Sparkles size={15} /></div>
                    <div className="chat-bubble chat-bubble-assistant">
                      <div className="chat-msg-text">Beschreibe, worum es geht — alles, was du hast. Ich prüfe auf Vollständigkeit und frage nach, falls etwas fehlt.</div>
                    </div>
                  </div>
                )}
                {messages.map((m) => <Message key={m.id} m={m} onNavigate={onNavigate} />)}
                {stage && (
                  <div className="chat-msg chat-msg-assistant">
                    <div className="chat-avatar chat-avatar-assistant"><Sparkles size={15} /></div>
                    <div className="chat-bubble chat-bubble-assistant chat-stage">
                      <Loader size={14} className="icon-spin" /> {stageText(stage)}
                    </div>
                  </div>
                )}
              </div>

              {/* Composer */}
              <div className="chat-composer">
                {session.mode === "review" && !hasResult ? (
                  <div className="chat-paste">
                    <textarea
                      className="chat-paste-text"
                      rows={6}
                      placeholder="Füge hier den fertigen Text ein, der geprüft werden soll …"
                      value={pasteText}
                      onChange={(e) => setPasteText(e.target.value)}
                    />
                    <textarea
                      className="chat-paste-context"
                      rows={2}
                      placeholder="Optional: Quelle / Kontext (worum geht's, welche Fakten gelten?) — aktiviert den Faktencheck"
                      value={pasteContext}
                      onChange={(e) => setPasteContext(e.target.value)}
                    />
                    <button className="btn-primary" onClick={runReview} disabled={busy || !pasteText.trim()}>
                      {busy ? <><Loader size={16} className="spin" /> Prüfe …</> : <><Sparkles size={16} /> Text prüfen</>}
                    </button>
                  </div>
                ) : session.mode === "generate" && !hasResult ? (
                  <>
                    {isReady ? (
                      <div className="chat-ready-bar">
                        <span className="chat-ready-note"><Check size={15} /> Bereit zum Generieren.</span>
                        <button className="btn-primary" onClick={() => runPipeline()} disabled={busy}>
                          {busy ? <><Loader size={16} className="spin" /> Generiere …</> : <><Sparkles size={16} /> Text generieren</>}
                        </button>
                      </div>
                    ) : null}
                    <div className="chat-composer-field">
                      <textarea
                        className="chat-input-box"
                        rows={1}
                        placeholder={isReady ? "Noch etwas ergänzen? (optional)" : "Beschreibe, worum es geht … (Enter zum Senden)"}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                      />
                      <button className="chat-send" onClick={sendMessage} disabled={busy || !input.trim()} title="Senden">
                        {busy ? <Loader size={17} className="spin" /> : <Send size={17} />}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="chat-done-bar">
                    <Check size={15} /> Fertig. <button className="chat-link-btn" onClick={startNewChat}>Neuen Chat starten</button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

function stageText(stage) {
  if (!stage || stage === "start") return "Arbeite …";
  if (stage === "reviewing") return "Prüfe den Text …";
  if (stage === "reference") return "Suche Stil-Referenz …";
  if (stage === "agent1" || stage === "writing") return "Schreibe Entwurf …";
  if (stage === "agents2-8") return "Prüfung (Runde 1) …";
  if (stage === "agent7") return "Überarbeitung (Runde 1) …";
  if (stage === "formatting") return "Formatierung …";
  if (stage === "hallucination-check") return "Faktentreue-Gate …";
  const rev = String(stage).match(/^review-(\d+)$/);
  if (rev) return `Prüfung (Runde ${rev[1]}) …`;
  const rw = String(stage).match(/^rewrite-(\d+)$/);
  if (rw) return `Überarbeitung (Runde ${rw[1]}) …`;
  return "Arbeite …";
}
